import React, { useState, useEffect } from 'react';
import {
  Card,
  Descriptions,
  Form,
  Input,
  Button,
  Space,
  Typography,
  Avatar,
  Row,
  Col,
  message,
  Upload,
  Image,
} from 'antd';
import { UserOutlined, UploadOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import api from '../config/api';

const { Title } = Typography;

const Profile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>((user as any)?.avatar_url);

  // Sync form with user data when user changes
  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        phone: user.phone || '',
        extension: user.extension || '',
        ringcentral_extension: (user as any)?.ringcentral_extension || '',
      });
      setAvatarUrl((user as any)?.avatar_url);
    }
  }, [user, form]);

  const updateProfileMutation = useMutation(
    (data: any) => api.put('/api/users/profile', data),
    {
      onSuccess: (response) => {
        // Update the user in AuthContext with the response data
        if (response.data) {
          updateUser(response.data);
        }
        queryClient.invalidateQueries('profile');
        message.success('Profile updated successfully');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to update profile');
      }
    }
  );

  const changePasswordMutation = useMutation(
    (data: { current_password: string; new_password: string }) => 
      api.put('/api/users/change-password', data),
    {
      onSuccess: () => {
        message.success('Password changed successfully');
        form.setFieldsValue({ password: '', current_password: '' });
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to change password');
      }
    }
  );

  const handleSubmit = async (values: any) => {
    const { password, current_password, ...profileData } = values;
    
    // If password is provided, change password separately
    if (password && password.trim() !== '') {
      if (!current_password || current_password.trim() === '') {
        message.error('Current password is required to change password');
        return;
      }
      
      // Change password
      try {
        await changePasswordMutation.mutateAsync({
          current_password,
          new_password: password
        });
      } catch (error) {
        // Error already handled in mutation
        return;
      }
    }
    
    // Remove empty strings and only send fields that have values
    const cleanedProfileData: any = {};
    Object.keys(profileData).forEach(key => {
      if (profileData[key] !== undefined && profileData[key] !== null && profileData[key] !== '') {
        cleanedProfileData[key] = profileData[key];
      }
    });
    
    // Update profile (only if there are fields to update)
    if (Object.keys(cleanedProfileData).length > 0) {
      updateProfileMutation.mutate(cleanedProfileData);
    } else if (!password) {
      // If no password change and no profile data, show message
      message.info('No changes to save');
    }
  };

  const handleAvatarUpload = async (file: File) => {
    try {
      if (!user?.id) return false;
      setAvatarUploading(true);
      const formData = new FormData();
      formData.append('avatar', file);
      const res = await api.post(`/api/users/${user.id}/avatar`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const newAvatarUrl = res.data?.avatarUrl || res.data?.avatar_url;
      setAvatarUrl(newAvatarUrl);
      // Update user context with new avatar
      if (newAvatarUrl) {
        updateUser({ avatar_url: newAvatarUrl });
      }
      queryClient.invalidateQueries('profile');
      message.success('Avatar updated');
      return false; // prevent antd auto upload
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Failed to upload avatar');
      return false;
    } finally {
      setAvatarUploading(false);
    }
  };

  return (
    <div style={{ padding: 24, background: '#f0f2f5', minHeight: 'calc(100vh - 64px)' }}>
      <Row gutter={24}>
        <Col xs={24} md={8}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              {avatarUrl ? (
                <Image width={120} height={120} src={avatarUrl} style={{ objectFit: 'cover', borderRadius: '50%', marginBottom: 12 }} />
              ) : (
                <Avatar size={120} icon={<UserOutlined />} style={{ backgroundColor: '#1976d2', marginBottom: 12 }} />
              )}
              <div>
                <Upload showUploadList={false} accept="image/*" beforeUpload={(file) => handleAvatarUpload(file as any)}>
                  <Button icon={<UploadOutlined />} loading={avatarUploading}>Upload Avatar</Button>
                </Upload>
              </div>
              <Title level={3}>{user?.first_name} {user?.last_name}</Title>
              <p style={{ color: '#666' }}>{user?.role?.toUpperCase()}</p>
            </div>
          </Card>

          <Card title="Information" style={{ marginTop: 16 }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Email">{user?.email}</Descriptions.Item>
              <Descriptions.Item label="Phone">{user?.phone || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Extension">{user?.extension || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Agent Level">{(user as any)?.agent_level || 'N/A'}</Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24} md={16}>
          <Card title="Edit Profile">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              initialValues={{
                first_name: user?.first_name,
                last_name: user?.last_name,
                email: user?.email,
                phone: user?.phone || '',
                extension: user?.extension || '',
                ringcentral_extension: (user as any)?.ringcentral_extension || '',
              }}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="first_name"
                    label="First Name"
                    rules={[{ required: true, message: 'Please enter first name' }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="last_name"
                    label="Last Name"
                    rules={[{ required: true, message: 'Please enter last name' }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="email"
                    label="Email"
                    rules={[
                      { required: true, message: 'Please enter email' },
                      { type: 'email', message: 'Please enter a valid email' }
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="phone"
                    label="Phone"
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="extension"
                    label="Extension"
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="ringcentral_extension"
                    label="RingCentral Extension"
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="current_password"
                label="Current Password (required to change password)"
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const newPassword = getFieldValue('password');
                      if (newPassword && newPassword.trim() !== '' && (!value || value.trim() === '')) {
                        return Promise.reject(new Error('Current password is required to change password'));
                      }
                      return Promise.resolve();
                    }
                  })
                ]}
              >
                <Input.Password placeholder="Enter current password" />
              </Form.Item>

              <Form.Item
                name="password"
                label="New Password (leave blank to keep current)"
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const currentPassword = getFieldValue('current_password');
                      if (value && value.trim() !== '' && (!currentPassword || currentPassword.trim() === '')) {
                        return Promise.reject(new Error('Current password is required'));
                      }
                      if (value && value.length < 6) {
                        return Promise.reject(new Error('Password must be at least 6 characters'));
                      }
                      return Promise.resolve();
                    }
                  })
                ]}
              >
                <Input.Password placeholder="Enter new password" />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    loading={updateProfileMutation.isLoading || changePasswordMutation.isLoading}
                  >
                    Update Profile
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Profile;
